import { useCallback } from "react";
import axiosInstance from "../../service/axiosInstance";
import { inventoryAPI } from "../../service/api";
import { GET } from "../constants";
import { IInventoryCollateral, IInventoryInput } from "../../interfaces";

const useInvCollaterals = () => {
  const getInvCollateralByBorrowerId = useCallback(async (borrowerId: number) => {
    try {
      const response = await axiosInstance.request({
        url: inventoryAPI.FIND_IS_ARCHIVED,
        method: GET,
        params: {
          borrowerId,
          isArchived: false,
          sortBy: 'invCollateralName,ASC'
        }
      });
      return response.data.content as IInventoryCollateral[];
    } catch (error) {
      console.log('GET INVENTORY COLLATERAL BY BORROWER ID ERROR: ', error);
      return [];
    }
  }, []);

  const getInvInputByInvCollateralId = useCallback(async (invCollateralId: number) => {
    try {
      const response = await axiosInstance.request({
        url: inventoryAPI.FIND_INPUT_BY_COLLATERAL,
        method: GET,
        params: {
          invCollateralId,
          sortBy: 'recordId,DESC'
        }
      });
      return response.data.content as IInventoryInput[];
    } catch (error) {
      console.log('GET INVENTORY INPUTS BY INVENTORY COLLATERAL ID ERROR: ', error);
      return [];
    }
  }, []);

  const getInvInputByBorrowerId = useCallback(async (borrowerId: number) => {
    try {
      const response = await axiosInstance.request({
        url: inventoryAPI.FIND_IS_ARCHIVED_BY_INPUT,
        method: GET,
        params: {
          borrowerId,
          isArchived: false
        }
      });
      return response.data.content as IInventoryInput[];
    } catch (error) {
      console.log('GET INVENTORY INPUTS BY INVENTORY BORROWER ID ERROR: ', error);
      return [];
    }
  }, []);

  const getInventoryCollateralOptions = useCallback(async (borrowerId: number) => {
    const inventoryCollaterals = await getInvCollateralByBorrowerId(borrowerId);
    const inventoryInputs = await getInvInputByBorrowerId(borrowerId);
    
    const collaterals = inventoryCollaterals.map(collateral => {
      const matchingInput = inventoryInputs.find(input => input.invCollateralFk === collateral.recordId);

      if (matchingInput) {
        return ({
          ...matchingInput,
          ...collateral,
        }) as IInventoryCollateral & IInventoryInput;
      }

      const defaultInput: IInventoryInput = {
        recordId: 0,
        borrowerFk: collateral.borrowerFk,
        invCollateralFk: collateral.recordId,
        invCollateralName: collateral.invCollateralName,
        advanceRate: 0,
        nolvAdvanceRate: 0,
        nolvAppraisal: 0,
        nolvIneligibleAmount: 0,
        sublimit: 0,
        sublimitType: '',
        amountLimitType: '',
        current: true,
        archive: false,
        currency: '150'
      }

      return ({ ...defaultInput, ...collateral }) as IInventoryCollateral & IInventoryInput;
    })

    return collaterals;
  }, [])

  const selectCollateralByParameter = useCallback((invCollateralId: number, collaterals: (IInventoryCollateral & IInventoryInput)[]) => {
    const collateralOnParameter = collaterals.find(collateral => collateral.recordId === invCollateralId);
    const selected = collateralOnParameter ?? null;
    return selected;
  }, [])

  return {
    getInvCollateralByBorrowerId,
    getInvInputByBorrowerId,
    getInvInputByInvCollateralId,
    getInventoryCollateralOptions,
    selectCollateralByParameter
  };
};

export default useInvCollaterals;
